import React, { useState, useEffect, useRef } from "react";
import "../styles/OUpload.scss";
import { uploadFile } from "../actions/filesActions";
import { useDispatch } from "react-redux";

// Helper function to simulate a loader - in a real app, you might use a library or a more complex implementation.
const $startLoader = () => console.log("Loader started");
const $stopLoader = () => console.log("Loader stopped");

const OUpload = ({
  multiple = false,
  imagesBaseUrl = "https://kmartidp.s3.ap-northeast-2.amazonaws.com", // Default or placeholder
  value = "",
  text = "Drag and drop image here, or click here to add image",
  size = "md",
  onImagesUploaded,
  onStartLoader = $startLoader,
  onStopLoader = $stopLoader,
  uploadUrl = "https://your-api-endpoint.com/upload", // Make this a prop
}) => {
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  // Effect to sync component state with the `value` prop
  useEffect(() => {
    if (value && value.length > 0) {
      setImages(Array.isArray(value) ? value : [value]);
    } else {
      setImages([]);
    }
  }, [value]);

  /**
   * Handles the file selection from the input.
   * @param e - The change event from the file input.
   */
  const handleUpload = (e) => {
    const files = e?.target.files || fileInputRef.current?.files;

    if (files && files.length > 0) {
      const newImages = Array.from(files);
      // Upload each selected image
      newImages.forEach((imageFile) => {
        uploadImage(imageFile);
      });
    }
  };

  /**
   * Removes an image from the preview list.
   * @param imageToRemove - The filename of the image to remove.
   */
  const removeImage = (imageToRemove) => {
    const updatedImages = images.filter((img) => img !== imageToRemove);
    setImages(updatedImages);
    // Notify parent component of the change
    onImagesUploaded?.(multiple ? updatedImages : updatedImages[0] || "");
  };

  /**
   * Simulates uploading an image file.
   * In a real application, this would involve a fetch call to a server.
   * @param imageFile - The file to upload.
   */
  const uploadImage = async (imageFile) => {
    setLoading(true);
    try {
      // Create FormData to send the file
      const formData = new FormData();
      formData.append("file", imageFile);
      const result = await dispatch(uploadFile(formData));
      const fileName = result?.file_new_name;
      if (fileName) {
        setImages((prevImages) => {
          const updatedImages = multiple
            ? [...prevImages, fileName]
            : [fileName];
          // Notify parent component with the new list of images
          onImagesUploaded?.(multiple ? updatedImages : fileName);
          return updatedImages;
        });
      } else {
        throw new Error("Upload failed: Missing file name in response");
      }
    } catch (err) {
      console.error("Something went wrong during upload", err);
      // Optionally, handle the error in the UI
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="OUpload">
      <div className="img-uploader">
        {/* Image preview */}
        <div className="preview">
          {images.map((image) => (
            <div className={`img-item ${size}`} key={image}>
              {image && image !== "" ? (
                <>
                  <img src={`${imagesBaseUrl}/${image}`} alt="Preview" />
                  <span
                    className="delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeImage(image);
                    }}
                  >
                    <i className="fas fa-trash"></i>
                  </span>
                </>
              ) : (
                <div className="img-placeholder">
                  {/* <i className="fas fa-file-image"></i> */}
                </div>
              )}
            </div>
          ))}
          {/* Dropzone */}
          <div
            className={`dropzone ${size}`}
            style={{ display: images.length < 1 ? "block" : "none" }}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex-col-center">
              <i className="fas fa-cloud-upload-alt"></i>
              <p>{text}</p>
              <button type="button" className="outline mybtn mybtn-default">
                Add Image{multiple && "s"}
              </button>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleUpload}
              multiple={multiple}
              accept="image/*,.pdf,.doc,.docx,.xml,application/msword,.xlsx,.xlsm,"
              style={{ display: "none" }}
            />
          </div>
          {/* Dropzone for when there's an existing image */}
          <div
            className={`dropzone2 ${size}`}
            style={{ display: images.length >= 1 ? "block" : "none" }}
            onClick={() => fileInputRef.current?.click()}
          >
            <span className={size}>{multiple ? "+" : "Change"}</span>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleUpload}
              multiple={multiple}
              accept="image/*,.pdf,.doc,.docx,.xml,application/msword,.xlsx,.xlsm,"
              style={{ display: "none" }}
            />
          </div>
        </div>
      </div>
      {loading && (
        <div className="loader-spinner">
          <div className="spinner-icon">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
        </div>
      )}
    </div>
  );
};

export default OUpload;
